import { FFmpeg } from '@ffmpeg/ffmpeg'
import { fetchFile, toBlobURL } from '@ffmpeg/util'
import { useCallback, useEffect, useRef, useState } from 'react'
import { isFirefox } from 'react-device-detect'

import type { Recording } from '~/types/recorder'

export function useVoiceRecorder(onRecordingComplete: (recording: Omit<Recording, 'id'>) => void) {
  const ffmpegRef = useRef(new FFmpeg())

  const loadFFmpeg = async () => {
    const baseURL = window.location.origin

    const ffmpeg = ffmpegRef.current
    ffmpeg.on('log', ({ message }) => {
      console.log('ffmpeg message', message)
    })

    return await ffmpeg
      .load({
        coreURL: await toBlobURL(`${baseURL}/ffmpeg/ffmpeg-core.js`, 'text/javascript'),
        wasmURL: await toBlobURL(`${baseURL}/ffmpeg/ffmpeg-core.wasm`, 'application/wasm'),
      }) // 0.12.10 esm (not multithread)
      .catch((error) => {
        console.log('error on loading ffmpeg', error)
      })
  }

  const transcodeAudioToMp4 = (audioBlob: Blob): Promise<Blob> =>
    new Promise((resolve, reject) => {
      const processTranscoding = async () => {
        if (!ffmpegRef.current.loaded) {
          await loadFFmpeg().catch((error) => {
            console.log('error on loading ffmpeg', error)
            reject(error)
          })
        }

        const audioURL = URL.createObjectURL(audioBlob)
        const inputFormat = audioBlob.type.includes('webm') ? 'webm' : 'mp4'

        await ffmpegRef.current.writeFile(`input.${inputFormat}`, await fetchFile(audioURL))
        await ffmpegRef.current.exec(['-i', `input.${inputFormat}`, 'output.mp4'])

        const data = await ffmpegRef.current.readFile('output.mp4')

        const mp4Blob = new Blob([(data as Uint8Array).buffer as ArrayBuffer], { type: 'video/mp4' })

        resolve(mp4Blob)
      }

      processTranscoding().catch((error) => {
        // TODO: add handling for FS error
        console.log('error on process transcoding', error)
        reject(error)
      })
    })

  const [isRecording, setIsRecording] = useState(false)
  const [audioPermissionStatus, setAudioPermissionStatus] = useState<'granted' | 'denied' | 'prompt'>('prompt')

  const chunksRef = useRef<BlobPart[]>([])
  const recorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)

  const startRecording = useCallback(
    async ({
      queueNo,
      counterRunId,
      queueSecretCode,
    }: {
      queueNo: string
      counterRunId: string
      queueSecretCode: string
    }) => {
      if (isRecording) return

      if (!ffmpegRef.current) {
        await loadFFmpeg()
      }

      let mimeType = 'audio/mp4'

      if (isFirefox) {
        mimeType = 'audio/ogg;codecs=opus'
      }

      let mediaRecorder: MediaRecorder

      // Check if the browser supports the MIME type
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        console.warn(`${mimeType} is not supported, falling back to browser default`)
        mediaRecorder = new MediaRecorder(streamRef.current as MediaStream)
        mimeType = mediaRecorder.mimeType
      } else {
        mediaRecorder = new MediaRecorder(streamRef.current as MediaStream, {
          mimeType,
        })
      }

      mediaRecorder.ondataavailable = (event) => {
        chunksRef.current.push(event.data)
      }
      mediaRecorder.onstop = (event) => {
        const blob = new Blob(chunksRef.current, { type: (event.currentTarget as MediaRecorder).mimeType })
        chunksRef.current = [] // Clear chunks

        onRecordingComplete({
          blob,
          timestamp: new Date().toISOString(),
          isUploaded: false,
          queueNo,
          queueSecretCode,
          counterRunId,
        })
      }

      recorderRef.current = mediaRecorder
      setIsRecording(true)
      recorderRef.current.start(1000)
    },
    [isRecording, onRecordingComplete],
  )

  const stopRecording = () => {
    if (!isRecording || !recorderRef.current) return
    setIsRecording(false)
    recorderRef.current.stop()
  }

  const checkMicrophonePermission = async () => {
    try {
      const permission = await navigator.permissions.query({ name: 'microphone' as PermissionName })
      setAudioPermissionStatus(permission.state)

      // Listen for permission changes
      permission.addEventListener('change', () => {
        setAudioPermissionStatus(permission.state)
      })
    } catch (error) {
      console.error('Error checking microphone permission:', error)
    }
  }

  useEffect(() => {
    checkMicrophonePermission().catch((error) => {
      console.error('Error checking microphone permission:', error)
    })

    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        streamRef.current = stream
      })
      .catch((error) => {
        console.error('Error getting user media:', error)
      })
  }, [])

  return {
    isRecording,
    startRecording,
    stopRecording,
    audioPermissionStatus,
    transcodeAudioToMp4,
  }
}
